import {
  Home,
  Send,
  MessageSquare,
  Users,
  FileText,
  BarChart,
  Settings,
  CreditCard,
  Zap,
  Menu,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import el from "../assets/el2.png";

const Sidebar = ({ darkMode, isMobile, isOpen, closeSidebar }) => {
  const [dropdowns, setDropdowns] = useState({
    chat: false,
  });

  const toggleDropdown = (key) => {
    setDropdowns((prev) => ({
      ...prev,
      [key]: !prev[key], // Only toggle the clicked dropdown, don't close others
    }));
  };

  // Close all dropdowns (only used for mobile sidebar close)
  const closeAllDropdowns = () => {
    setDropdowns({
      chat: false,
    });
  };

  const location = useLocation();

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside sidebar container and sidebar is open on mobile
      if (isMobile && isOpen && !event.target.closest('.sidebar-container')) {
        closeSidebar();
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    // Also handle touch events for mobile devices
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobile, isOpen, closeSidebar]);

  // Close dropdowns when route changes (only if not active)
  useEffect(() => {
    // Check if current route is not in any submenu
    const isInChatSubmenu = ['/history'].includes(location.pathname);
    
    if (!isInChatSubmenu) {
      setDropdowns({
        chat: false,
      });
    }
  }, [location.pathname]);

  // Handle main menu item click - DON'T close dropdowns
  const handleMainMenuClick = () => {
    // Only close sidebar on mobile, but keep dropdowns open
    if (isMobile) {
      closeSidebar();
    }
  };

  // Handle submenu item click
  const handleSubmenuClick = () => {
    // Close sidebar on mobile but keep dropdown open
    if (isMobile) {
      closeSidebar();
    }
  };

  // Close sidebar when pressing Escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isMobile && isOpen) {
        closeSidebar();
        closeAllDropdowns();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isMobile, isOpen, closeSidebar]);

  return (
    <>
      <style>{`
        .sidebar-container {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
        
        .sidebar-container::-webkit-scrollbar {
          display: none; /* Chrome, Safari and Opera */
        }
        
        .sidebar-link {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 8px;
          position: relative;
          overflow: hidden;
        }
        
        .sidebar-link::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 3px;
          background: linear-gradient(180deg, #25D366, #22C55E);
          transform: scaleY(0);
          transition: transform 0.3s ease;
        }
        
        .sidebar-link:hover::before {
          transform: scaleY(1);
        }
        
        .sidebar-link:hover {
          background: rgba(255, 255, 255, 0.05);
          transform: translateX(4px);
        }
        
        .active-sidebar {
          background: linear-gradient(135deg, #25D366, #22C55E) !important;
          color: #fff !important;
          box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3);
          transform: translateX(4px);
        }
        
        .active-sidebar::before {
          transform: scaleY(1);
          background: #fff;
        }
        
        .submenu-active {
          background: rgba(37, 211, 102, 0.1) !important;
          color: #25D366 !important;
          font-weight: 600;
          border-left: 3px solid #25D366;
        }
        
        .sidebar-container {
          backdrop-filter: blur(10px);
          border-right: 1px solid rgba(255, 255, 255, 0.1);
          overflow-y: auto;
        }
        
        /* Mobile overlay styles */
        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 35;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @media (max-width: 767px) {
          .sidebar-container {
            box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
            width: 280px !important;
          }
        }

        /* Smooth transitions for mobile */
        @media (max-width: 768px) {
          .sidebar-container {
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
        }
        
        /* Prevent body scroll when sidebar is open on mobile */
        body.sidebar-open {
          overflow: hidden;
        }
      `}</style>

      {/* Overlay (Mobile) - This will close sidebar when clicked */}
      {isOpen && isMobile && (
        <div
          className="sidebar-overlay lg:hidden"
          onClick={() => {
            closeSidebar();
            closeAllDropdowns();
          }}
        />
      )}

      {/* Sidebar */}
      <div
  className={`sidebar-container fixed top-0 left-0 h-full w-80 flex flex-col p-6 transform transition-all duration-300 ease-in-out z-40 overflow-y-auto ${
    isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
  }`}

        style={{
          backgroundColor: "rgba(18, 20, 22, 0.95)",
          color: "#f8fafc",
          fontFamily: "Inter, sans-serif",
          width: "280px",
        }}
      >
        {/* Logo Section */}
        <div className="flex items-center justify-between mb-8 mt-2 pb-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#25D366] to-[#22C55E] rounded-xl shadow-lg">
              <img src={el} alt="SendApp" className="h-6 w-6 sm:h-7 sm:w-7" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-[#25D366] to-[#22C55E] bg-clip-text text-transparent">
                SendApp
              </h1>
              <p className="text-xs text-gray-400">Business Messaging</p>
            </div>
          </div>
          
          {/* Close button for mobile */}
          {/* {isMobile && (
            <button
              onClick={() => {
                closeSidebar();
                closeAllDropdowns();
              }}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors duration-200 md:hidden"
              aria-label="Close sidebar"
            >
              <Menu size={18} />
            </button>
          )} */}
        </div>

        {/* Navigation Menu */}
        <nav className="flex flex-col space-y-1 flex-1">
          <NavItem
            icon={<Home size={18} />}
            label="Dashboard"
            path="/dashboard"
            location={location}
            onClick={handleMainMenuClick}
          />

          <NavItem
            icon={<Send size={18} />}
            label="Compose Message"
            path="/compose-message"
            location={location}
            onClick={handleMainMenuClick}
          />

          <DropdownItem
            icon={<MessageSquare size={18} />}
            label="Chat"
            isOpen={dropdowns.chat}
            toggleDropdown={() => toggleDropdown("chat")}
            subItems={[
              { label: "History", path: "/history" },
              {label: "Live Chat", path: "/livechat" },
            ]}
            location={location}
            onSubmenuClick={handleSubmenuClick}
          />

          <NavItem
            icon={<Users size={18} />}
            label="Contacts"
            path="/allcontacts"
            location={location}
            onClick={handleMainMenuClick}
          />

          <NavItem
            icon={<FileText size={18} />}
            label="Manage Template"
            path="/templates"
            location={location}
            onClick={handleMainMenuClick}
          />

          <NavItem
            icon={<BarChart size={18} />}
            label="Reports"
            path="/Reports"
            location={location}
            onClick={handleMainMenuClick}
          />

          <NavItem
            icon={<CreditCard size={18} />}
            label="Billing"
            path="/billing"
            location={location}
            onClick={handleMainMenuClick}
          />

          <NavItem
            icon={<Zap size={18} />}
            label="Flow"
            path="/flow"
            location={location}
            onClick={handleMainMenuClick}
          />
        </nav>

        {/* User/Settings Section */}
        <div className="pt-4 mt-4 border-t border-gray-700">
          <NavItem
            icon={<Settings size={18} />}
            label="Settings"
            path="/support"
            location={location}
            onClick={handleMainMenuClick}
          />
        </div>
      </div>
    </>
  );
};

/* ----- NavItem Component ----- */
const NavItem = ({ icon, label, path, location, onClick }) => {
  const active = location.pathname === path;
  
  return (
    <Link
      to={path}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 sidebar-link ${
        active 
          ? "active-sidebar text-white" 
          : "text-gray-300 hover:text-white"
      }`}
    >
      <div className={`transition-transform duration-200 ${active ? 'scale-110' : ''}`}>
        {icon}
      </div>
      <span className="text-sm font-medium tracking-wide">{label}</span>
    </Link>
  );
};

/* ----- DropdownItem Component ----- */
const DropdownItem = ({
  icon,
  label,
  isOpen,
  toggleDropdown,
  subItems,
  location,
  onSubmenuClick,
}) => {
  const activeParent = subItems.some((item) => location.pathname === item.path);

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className={`flex items-center justify-between w-full px-3 py-2.5 sidebar-link ${
          activeParent 
            ? "active-sidebar text-white" 
            : "text-gray-300 hover:text-white"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`transition-transform duration-200 ${activeParent ? 'scale-110' : ''}`}>
            {icon}
          </div>
          <span className="text-sm font-medium tracking-wide">{label}</span>
        </div>
        <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown size={16} />
        </div>
      </button>

      {isOpen && (
        <div className="ml-3 mt-1 space-y-1 pl-3 border-l border-gray-600">
          {subItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={index}
                to={item.path}
                onClick={onSubmenuClick}
                className={`block text-sm px-3 py-2 rounded-lg transition-all duration-200 ${
                  isActive
                    ? "submenu-active shadow-sm"
                    : "text-gray-400 hover:text-white hover:bg-gray-800"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    isActive ? 'bg-[#25D366]' : 'bg-gray-500'
                  }`} />
                  {item.label}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Sidebar;